import { BizAppConfig } from './application.manifest';

export type BrandLocale = 'zh-CN' | 'zh-TW' | 'en-US' | 'ja-JP';

type BrandTextKey = 'appName' | 'footerCopyright';
type BrandLinkRel = 'icon' | 'shortcut icon' | 'apple-touch-icon';

const BRAND_LOCALE_STORAGE_KEYS = ['umi_locale', 'app_locale'] as const;

const BRAND_LOCALES: BrandLocale[] = ['zh-CN', 'zh-TW', 'en-US', 'ja-JP'];
const BRAND_ICON_LINK_RELS: BrandLinkRel[] = [
  'icon',
  'shortcut icon',
  'apple-touch-icon',
];

const isBrandLocale = (locale?: string): locale is BrandLocale =>
  Boolean(locale && BRAND_LOCALES.includes(locale as BrandLocale));

export const normalizeBrandLocale = (locale?: string): BrandLocale =>
  isBrandLocale(locale) ? locale : 'zh-CN';

export const getStoredBrandLocale = (): BrandLocale => {
  if (typeof window === 'undefined') {
    return 'zh-CN';
  }

  const storedLocale = BRAND_LOCALE_STORAGE_KEYS.map((key) =>
    window.localStorage.getItem(key),
  ).find(Boolean);

  return normalizeBrandLocale(storedLocale || undefined);
};

const getBrandText = (
  key: BrandTextKey,
  locale?: string,
  fallback?: string,
): string => {
  const normalizedLocale = normalizeBrandLocale(
    locale || getStoredBrandLocale(),
  );
  const localizedText =
    key === 'appName'
      ? BizAppConfig.appNames[normalizedLocale]
      : BizAppConfig.appFooterCopyrights[normalizedLocale];

  return localizedText || fallback || '';
};

export const getBrandAppName = (locale?: string): string =>
  getBrandText('appName', locale, BizAppConfig.appName);

export const getBrandFooterCopyright = (locale?: string): string =>
  getBrandText('footerCopyright', locale, BizAppConfig.appFooterCopyright);

export const getBrandAppLogo = (): string => '/logo.png';

export const getBrandHeadLinks = (): Array<{
  rel: BrandLinkRel;
  href: string;
}> => {
  const href = getBrandAppLogo();

  if (!href) {
    return [];
  }

  return BRAND_ICON_LINK_RELS.map((rel) => ({
    rel,
    href,
  }));
};

export const syncBrandDocument = (locale?: string): string => {
  const nextTitle = getBrandAppName(locale);

  if (typeof document === 'undefined') {
    return nextTitle;
  }

  if (document.title !== nextTitle) {
    document.title = nextTitle;
  }

  const nextIconHref = getBrandAppLogo();
  if (!nextIconHref) {
    return nextTitle;
  }

  BRAND_ICON_LINK_RELS.forEach((rel) => {
    let link = document.head.querySelector(
      `link[rel="${rel}"]`,
    ) as HTMLLinkElement | null;

    if (!link) {
      link = document.createElement('link');
      link.rel = rel;
      document.head.appendChild(link);
    }

    if (link.getAttribute('href') !== nextIconHref) {
      link.setAttribute('href', nextIconHref);
    }
  });

  return nextTitle;
};
